import { DatePicker, type DatePickerValue, FieldError } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useId } from 'react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import Required from '~/components2/forms/Required';

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  current?: string;
  minDate?: DateTime;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputClassName?: string;
  validate?: (value: PathValue<T, Path<T>>, formValues: T) => string | boolean;
  'data-testid'?: string;
}

function toDatePickerValue(value: DateTime | null): DatePickerValue | null {
  if (!value || !value.isValid) return null;
  const iso = value.toISO();
  if (!iso) return null;
  return {
    date: iso,
    view: 'Day',
  };
}

function fromDatePickerValue(value: DatePickerValue | null): DateTime | null {
  if (!value || !('date' in value)) return null;
  const dt = DateTime.fromISO(value.date);
  return dt.isValid ? dt : null;
}

export default function ControlledDatePicker<T extends FieldValues>({
  control,
  name,
  label,
  minDate,
  required,
  disabled,
  placeholder = 'Select Date',
  inputClassName = '',
  validate,
  'data-testid': testId,
}: Props<T>) {
  const id = useId();
  const errorId = useId();

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required === true ? `${label} is required` : false,
        ...(validate && { validate }),
      }}
      render={({ field, fieldState: { error } }) => (
        <div className="relative flex w-full flex-col font-noto" data-testid={testId}>
          <div
            className={classNames('flex flex-col gap-1 w-full', error && 'data-[has-error=true]')}
            data-has-error={!!error}
          >
            <label htmlFor={id} className="text-xs capitalize">
              {label}
              <Required isRequired={required} />
            </label>
            <DatePicker
              id={id}
              required={required}
              value={toDatePickerValue(field.value)}
              onChange={(value) => {
                if (!disabled) {
                  field.onChange(fromDatePickerValue(value));
                }
              }}
              placeholder={placeholder}
              minDate={minDate?.toJSDate()}
              disabled={disabled}
              aria-invalid={!!error || undefined}
              aria-describedby={error ? errorId : undefined}
              inputClassName={classNames(inputClassName, error && 'border-red-500')}
              className="w-full max-w-full"
            />
            {!!error && (
              <FieldError id={errorId} className="text-xs" data-testid={`${testId ?? name}-error-details`}>
                {error.message}
              </FieldError>
            )}
          </div>
        </div>
      )}
    />
  );
}
